import { useMemo, useState, useContext } from "react";
import { useFormik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { VerifyContext } from "../../../../app/VerifyContext";
import { Lock, Mail, Loader2 } from "lucide-react";

const formSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string()
    .required("Password is required"),
});

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginUser } = useContext(VerifyContext);
  const [loginError, setLoginError] = useState("");

  const userType = useMemo<"admin" | "employee">(() => {
    return location.pathname.startsWith("/admin") ? "admin" : "employee";
  }, [location.pathname]);

  const handleSubmit = async (
    values: { email: string; password: string },
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    setLoginError("");
    const result = await loginUser(values.email, values.password, userType);
    if (result.success) {
      navigate("/");
    } else {
      setLoginError(
        result.message || "Login failed. Please check your credentials."
      );
    }
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: formSchema,
    onSubmit: handleSubmit,
  });

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary mb-4">
            <Lock className="w-7 h-7 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground font-inter">
            HRMS {userType === "admin" ? "Admin" : "Employee"}
          </h1>
          <p className="text-muted-foreground text-sm mt-1 font-inter">
            {userType === "admin"
              ? "Sign in to manage your organization"
              : "Sign in to access your account"}
          </p>
        </div>

        {/* Card */}
        <div className="bg-surface border border-border rounded-2xl p-8">
          <form onSubmit={formik.handleSubmit} noValidate>
            {/* Email */}
            <div className="mb-5">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-muted-foreground mb-2 font-inter"
              >
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder={
                    userType === "admin" ? "admin@hrms.com" : "employee@hrms.com"
                  }
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  className="w-full pl-10 pr-4 py-3 bg-surface-muted border border-border rounded-xl text-foreground text-sm font-inter placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                />
              </div>
              {formik.errors.email && formik.touched.email && (
                <p className="text-destructive text-xs mt-1.5 font-inter">
                  {formik.errors.email}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="mb-6">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-muted-foreground mb-2 font-inter"
              >
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="password"
                  id="password"
                  name="password"
                  placeholder="Enter your password"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  className="w-full pl-10 pr-4 py-3 bg-surface-muted border border-border rounded-xl text-foreground text-sm font-inter placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                />
              </div>
              {formik.errors.password && formik.touched.password && (
                <p className="text-destructive text-xs mt-1.5 font-inter">
                  {formik.errors.password}
                </p>
              )}
            </div>

            {/* Error */}
            {loginError && (
              <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-xl">
                <p className="text-destructive text-sm text-center font-inter">
                  {loginError}
                </p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={formik.isSubmitting}
              className="w-full py-3 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground font-medium text-sm rounded-xl font-inter transition-colors flex items-center justify-center gap-2"
            >
              {formik.isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-muted-foreground text-xs mt-6 font-inter">
          Human Resource Management System
        </p>
      </div>
    </div>
  );
}
